import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable, OnDestroy } from "@angular/core";
import { concat, firstValueFrom, Observable, of, Subject, Subscription } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { Brand } from "src/app/models/Brand";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import { Address } from "../models/Address";
import { Company } from "../models/Company";
import { DepartmentFilterType } from "../models/DepartmentFilterType";
import { BrandType, LoginStatus } from "../models/Enums";
import { Department } from "./../models/Department";
import { AuthService } from "./auth.service";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class CompanyService implements OnDestroy {
  companies: Company[];
  companies$: Observable<Company[]>;
  companiesLoading = false;
  companyInput$ = new Subject<string>();
  selectedCompany: Company;
  companyChange: EventEmitter<Company> = new EventEmitter<Company>();
  minLengthTerm = 3;

  private _subscriptions$: Subscription[] = [];

  private baseUrl_V2 = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/company`;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private authService: AuthService,
  ) {
    this.setInitialCompany();
    this.loadCompanies();

    this._subscriptions$.push(this.onLoginChange());
  }

  private onLogin() {
    this.setInitialCompany();
    this.loadCompanies();
  }

  private onLogout() {
    this.storage.cleanCompanyStorage();
  }

  public async getAllFamCodes(): Promise<string[]> {
    return await firstValueFrom(this.http.get<string[]>(`${this.baseUrl_V2}/allFamCodes`));
  }

  onLoginChange(): Subscription {
    return this.authService.isUserProfileSet$.subscribe((isUserProfileSet: LoginStatus) => {
      switch (isUserProfileSet) {
        case LoginStatus.LoggedIn:
          this.onLogin();
          return;

        case LoginStatus.LoggedOut:
          this.onLogout();
          return;
      }
    });
  }

  setCompanyToStorage(company: Company) {
    if (!company) {
      return;
    }

    company.region = this.authService.user.currentRegion;
    this.storage.setCompany(company);
    this.companyChange.emit(company);
  }

  onSelectionChange(company?: Company) {
    if (!company) return;
    this.selectedCompany = company;
    this.setDefaultDepartments(this.selectedCompany);
    this.setCompanyToStorage(this.selectedCompany);
  }

  onDepartmentSelectionChange(department: Department) {
    if (!department) return;
    this.selectedCompany.department = department;
    this.setCompanyToStorage(this.selectedCompany);
  }

  public unselectCompany(): void {
    this.selectedCompany = null;
    this.storage.cleanCompanyStorage();
  }

  private setInitialCompany() {
    const user = this.authService.user;
    const hasUserCompanies = user?.companies?.length > 0;
    const company = this.storage.company;

    // 1) Set from storage if exists if they are form the same region
    if (company && company.region === user?.currentRegion) {
      this.selectedCompany = company;
    }

    // remove the selected company of it's from a  different region
    if (this.selectedCompany && this.selectedCompany.region !== user?.currentRegion) {
      this.selectedCompany = undefined;
    }

    // 2) Set first as default from user companies list if any
    if (!this.selectedCompany && hasUserCompanies) {
      const defaultCompany = user.companies[0] as Company;
      this.setDefaultDepartments(defaultCompany);
      this.setCompanyToStorage(defaultCompany);
      this.selectedCompany = defaultCompany;
    }

    this.setInitialDropdownCompanies();

    this.companyChange.emit(this.selectedCompany);
  }

  /** Set user companies to companies list for company select dropdown */
  private setInitialDropdownCompanies() {
    const user = this.authService.user;
    if (user?.companies?.length > 0) {
      this.companies = user?.companies;
    } else {
      this.companies = [this.selectedCompany];
    }
  }

  search(query) {
    return this.http
      .post<Company[]>(this.baseUrl_V2 + "/search", {
        search: query,
      })
      .pipe(map((companies) => companies.map((x) => new Company().deserialize(x))));
  }

  getFamCodes() {
    return this.http.get<string[]>(this.baseUrl_V2 + "/famCodes");
  }

  public getDepartments(companyId: string): Observable<Department[]> {
    return this.http
      .get<Department[]>(this.baseUrl_V2 + "/" + companyId + "/department")
      .pipe(map((departments) => departments.map((x) => new Department().deserialize(x))));
  }

  getShipFromList(companyId: string): Observable<Address[]> {
    return this.http.get<Address[]>(`${this.baseUrl_V2}/${companyId}/shipFromList`);
  }

  getVendorToList(companyId) {
    return this.http.get<string[]>(this.baseUrl_V2 + "/" + companyId + "/vendorList");
  }

  getCountriesByBrand(brand: BrandType) {
    return this.http.get<Brand[]>(`${this.baseUrl_V2}/${brand}/countries`);
  }

  // TODO: Change loading of companies for different users
  // For normal users we load all companies they have access to
  // For admin users load only based on autocomplete
  loadCompanies() {
    this.companies$ = concat(
      of(this.companies.filter((x) => Boolean(x))), // default items (filter undefined items)
      this.companyInput$.pipe(
        filter((res) => {
          return res !== null && res?.length >= this.minLengthTerm;
        }),
        distinctUntilChanged(),
        debounceTime(500),
        tap(() => (this.companiesLoading = true)),
        switchMap((term) => {
          return this.search(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.companiesLoading = false)),
          );
        }),
      ),
    );
  }

  private setDefaultDepartments(company: Company) {
    this.ensureNoDepartmentOptionForAdmins(company);

    this.ensureAllDepartmentOption(company);

    const availableDepartments = company.departments.filter(
      (x) => x.departmentFilterType !== DepartmentFilterType.AllDepartments,
    );

    company.department =
      availableDepartments.length == 1
        ? availableDepartments[0]
        : company.departments.find(
            (x) => x.departmentFilterType === DepartmentFilterType.AllDepartments,
          );
  }

  /** For admins NoDepartment option does not come from their permissions */
  private ensureNoDepartmentOptionForAdmins(company: Company) {
    const isAdminProfile = this.authService.user?.isAdminGroupProfile;
    if (!isAdminProfile) return;

    const hasNoDepartmentOption = company.departments.some(
      (x) => x.departmentFilterType === DepartmentFilterType.NoDepartment,
    );
    if (hasNoDepartmentOption) return;

    company.departments.unshift(new Department(null, null, DepartmentFilterType.NoDepartment));
  }

  private ensureAllDepartmentOption(company: Company) {
    const hasAllDepartmentOption = company.departments.some(
      (x) => x.departmentFilterType === DepartmentFilterType.AllDepartments,
    );
    if (hasAllDepartmentOption) return;

    company.departments.unshift(new Department(null, null, DepartmentFilterType.AllDepartments));
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
